<script setup lang="ts">
import type { Page, PageBlock } from '#shared/types/schema';
import { withLeadingSlash, withoutTrailingSlash } from 'ufo';

const route = useRoute();
const { enabled, state } = useLivePreview();
const pageUrl = useRequestURL();
const { isVisualEditingEnabled, apply, setAttr } = useVisualEditing();

const permalink = withoutTrailingSlash(withLeadingSlash(route.path));

// Escludi le route API - devono essere gestite dal server, non dalla pagina catch-all
// In Nuxt, le route API nel server hanno automaticamente priorità, ma durante il SSR
// la pagina catch-all potrebbe comunque essere eseguita, quindi la escludiamo esplicitamente
const isApiRoute = permalink.startsWith('/api/');

// Escludi asset PWA (es. /dev-sw.js richiesto da @vite-pwa in dev) per evitare 404 in console
const isPwaAsset = permalink === '/dev-sw.js' || permalink.startsWith('/dev-sw');

// Escludi le route di autenticazione e area riservata dal fetch delle pagine
const authRoutes = ['/login', '/forgot-password', '/reset-password', '/area-riservata'];
const isAuthRoute = authRoutes.some(authRoute => permalink.startsWith(authRoute));

// Se è una route di autenticazione, API o asset PWA, non fare assolutamente nulla
// Crea un oggetto dummy che non farà mai fetch
// IMPORTANTE: Non chiamare useFetch per route di autenticazione, nemmeno durante SSR
const pageData = (isAuthRoute || isApiRoute || isPwaAsset)
	? {
			data: ref<Page | null>(null),
			error: ref<any>(null),
			refresh: async () => {},
			pending: ref(false),
			status: ref<'idle' | 'pending' | 'error' | 'success'>('idle'),
			execute: async () => {},
		}
	: await useFetch<Page>('/api/pages/one', {
			key: `pages-${permalink}`,
			query: {
				permalink,
				preview: enabled.value ? true : undefined,
				token: enabled.value ? state.token : undefined,
			},
			// Assicurati che non venga eseguito durante SSR se è una route di autenticazione o PWA
			server: !isAuthRoute && !isApiRoute && !isPwaAsset,
		});

const { data: page, error, refresh } = pageData;

// Se è una route di autenticazione, API o asset PWA, non controllare errori o page
if (isAuthRoute || isApiRoute || isPwaAsset) {
	// Non fare nulla, lascia che le route specifiche o il server gestiscano
} else if (error.value) {
	// Se l'errore è un 404, lancialo come 404
	if (error.value.statusCode === 404) {
		throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true });
	}
	// Altrimenti, lancia un errore 500
	throw createError({ 
		statusCode: error.value.statusCode || 500, 
		statusMessage: error.value.statusMessage || 'Failed to load page', 
		fatal: true 
	});
} else if (!page.value) {
	throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true });
}

// Se è una route di autenticazione, API o asset PWA, non eseguire nessun altro codice
if (isAuthRoute || isApiRoute || isPwaAsset) {
	// Le route specifiche o il server gestiranno il rendering
	// Non eseguire nessun altro codice per evitare errori
} else {
	// Solo per le route normali, esegui il resto del codice
	const pageBlocks = computed(() => (page.value?.blocks as PageBlock[]) || []);
	const isTransparentNav = computed(() => page.value?.main_navigation_style === 'transparent');
	const isFullscreenDisplay = computed(() => page.value?.fullscreen_display === true);

	// Fornisce il valore fullscreen_display al layout tramite useState per condividerlo tra componenti
	const pageFullscreenState = useState('pageFullscreenDisplay', () => false);
	watch(isFullscreenDisplay, (value) => {
		pageFullscreenState.value = value;
	}, { immediate: true });

	useSeoMeta({
		title: page.value?.seo?.title || page.value?.title || '',
		description: page.value?.seo?.meta_description || '',
		ogTitle: page.value?.seo?.title || page.value?.title || '',
		ogDescription: page.value?.seo?.meta_description || '',
		ogUrl: pageUrl.toString(),
	});
}

// Helper functions for Visual Editing (solo per route normali)
if (!isAuthRoute && !isApiRoute && !isPwaAsset) {
	function applyVisualEditing() {
		apply({
			onSaved: async () => {
				await refresh();
			},
		});
	}

	function applyVisualEditingButton() {
		apply({
			elements: document.querySelector('#visual-editing-button') as HTMLElement,
			customClass: 'visual-editing-button-class',
			onSaved: async () => {
				await refresh();
				// This makes sure the visual editor elements are updated after the page is refreshed. In case you've added new blocks to the page.
				await nextTick();
				applyVisualEditing();
			},
		});
	}

	onMounted(() => {
		if (!isVisualEditingEnabled.value) return;
		applyVisualEditingButton();
		applyVisualEditing();
	});
}
</script>

<template>
	<!-- Se è una route di autenticazione, API o asset PWA, non renderizzare nulla -->
	<div v-if="!isAuthRoute && !isApiRoute && !isPwaAsset && page" class="relative">
		<PageBuilder v-if="page?.blocks" :sections="(page.blocks as PageBlock[])" :is-transparent-nav="page?.main_navigation_style === 'transparent'" :page-id="page?.id" />
		<div
			v-if="isVisualEditingEnabled && page"
			class="fixed z-50 w-full bottom-4 left-0 right-0 p-4 flex justify-center items-center gap-2"
		>
			<!-- If you're not using the visual editor it's safe to remove this element. Just a helper to let editors add edit / add new blocks to a page. -->
			<Button
				id="visual-editing-button"
				variant="secondary"
				:data-directus="
					setAttr({ collection: 'pages', item: page.id, fields: ['blocks', 'meta_m2a_button'], mode: 'modal' })
				"
			>
				<Icon name="lucide:pencil" />
				Edit All Blocks
			</Button>
		</div>
	</div>
</template>

<style>
.directus-visual-editing-overlay.visual-editing-button-class .directus-visual-editing-edit-button {
	/* Not using style scoped because the visual editor adds it's own elements to the page. Safe to remove this if you're not using the visual editor. */
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	transform: none;
	background: transparent;
}
</style>
